import { DsQueryFilterOperator } from '@common/result/data-source/enum/ds-query-filter-operator';
import { PlatformDataSourceQuery } from '@data/_generated-api/platform-api/models/platform-data-source-query';
import { PlatformFeedbackStatus } from '@data/_generated-api/platform-api/models/platform-feedback-status';
import { PlatformFeedbackType } from '@data/_generated-api/platform-api/models/platform-feedback-type';
import { SupportFeedbackScope } from './support-feedback-detail.component';
import { SUPPORT_OPEN_STATUSES } from './support-feedback.display';

/** Meldingen per pagina in de overzichten (melder en beheer). */
export const SUPPORT_PAGE_SIZE = 9;

/** Gelezen-filter, bekeken vanuit de opvrager: alles, alleen ongelezen of alleen gelezen. */
export type SupportReadFilter = 'all' | 'unread' | 'read';

/** Volgorde zoals de filterbalk ze toont. */
export const SUPPORT_READ_FILTERS: readonly SupportReadFilter[] = ['all', 'unread', 'read'];

/** Toestand van een meldingenoverzicht: filters, zoekterm en pagina (1-based). */
export interface SupportFeedbackQuery {
  /** Leeg = alle statussen. */
  statuses: PlatformFeedbackStatus[];
  /** Undefined = alle types. */
  type?: PlatformFeedbackType;
  read: SupportReadFilter;
  /** Zoekterm op de titel, zoals getypt (de query trimt). */
  search: string;
  page: number;
}

/** Startstand: de melder ziet al zijn meldingen, het beheer de open meldingen. */
export function defaultSupportFeedbackQuery(scope: SupportFeedbackScope): SupportFeedbackQuery {
  return {
    statuses: scope === 'manage' ? [...SUPPORT_OPEN_STATUSES] : [],
    type: undefined,
    read: 'all',
    search: '',
    page: 1,
  };
}

/** Beperkt de query de lijst? Bepaalt of een lege lijst "niets gevonden" of "nog niets" betekent. */
export function isSupportQueryFiltered(query: SupportFeedbackQuery): boolean {
  return query.statuses.length > 0 || query.type !== undefined || query.read !== 'all' || query.search.trim() !== '';
}

/** Gelezen-filter als API-parameter: undefined = niet filteren. */
export function supportUnreadParam(read: SupportReadFilter): boolean | undefined {
  return read === 'all' ? undefined : read === 'unread';
}

/**
 * DataSourceQuery voor de overzichtsendpoints: paging (1-based, {@link SUPPORT_PAGE_SIZE} per pagina)
 * en zoeken op de titel (`Summary`, hoofdletterongevoelig). Bewust zonder SortOptions: de backend
 * ordent dan zelf (beheer: prioriteit eerst, lege prioriteit achteraan, wat een SortOption niet kan
 * uitdrukken). Status, type en gelezen gaan als aparte parameters mee.
 */
export function buildSupportDataSourceQuery(query: SupportFeedbackQuery): PlatformDataSourceQuery {
  const search = query.search.trim();
  return {
    pageOption: { currentPageIndex: Math.max(1, Math.floor(query.page)), pageSize: SUPPORT_PAGE_SIZE },
    sortOptions: [],
    filterOptionGroup: search
      ? {
          logicalOperator: '',
          filterOptions: [{ propertyName: 'Summary', operator: DsQueryFilterOperator.StringContains, value: search }],
          filterOptionGroups: [],
        }
      : null,
  };
}
